import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Modal, message } from 'antd';
import { SendOutlined, RocketOutlined, CheckCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient from '../../services/api';
import BrainstormSessionPanel from './BrainstormSessionPanel';

const { TextArea } = Input;

/**
 * Format a brainstorm result into a readable string for conversation history.
 */
const formatResultForHistory = (result) => {
  const parts = [];
  if (result.rewrite?.summary) {
    parts.push(`Summary: ${result.rewrite.summary}`);
  }
  if (result.rewrite?.problem) {
    parts.push(`Problem: ${result.rewrite.problem}`);
  }
  if (result.rewrite?.deliverable) {
    parts.push(`Deliverable: ${result.rewrite.deliverable}`);
  }
  if (result.clarifying_questions?.length > 0) {
    const qs = result.clarifying_questions.map((q, i) => `${i + 1}. ${q.question}`).join('\n');
    parts.push(`Clarifying Questions:\n${qs}`);
  }
  parts.push(`Ready to brainstorm: ${result.ready_to_brainstorm ? 'yes' : 'no'}`);
  return parts.join('\n');
};

/**
 * BrainstormPanel - AI Ideas Brainstorm with task definition flow
 *
 * Flow:
 * 1. User inputs a task definition
 * 2. Calls brainstorm_define_task endpoint with conversation history
 * 3. Displays summary + clarifying questions
 * 4. User can answer questions / refine, history is preserved
 */
const BrainstormPanel = ({ selectedNote }) => {
  const { t } = useTranslation(['dashboard', 'messages', 'common']);
  const contentRef = useRef(null);

  const [taskInput, setTaskInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // The first user input, stored and reused as taskDefinition for all subsequent calls
  const [taskDefinition, setTaskDefinition] = useState(null);

  // The AI result from the initial task definition call (not part of turns)
  const [initialResult, setInitialResult] = useState(null);

  // Conversation turns (follow-up exchanges only): [{ userMessage: string, aiResult: object }]
  const [turns, setTurns] = useState([]);

  // Brainstorm session state
  const [isBrainstorming, setIsBrainstorming] = useState(false);
  const [savedTaskDefinition, setSavedTaskDefinition] = useState(null);

  // Check for existing brainstorm_define_task conversation on mount
  useEffect(() => {
    if (!selectedNote?.id) return;
    const checkExisting = async () => {
      try {
        const res = await apiClient.get(`/notes/conversations/${selectedNote.id}?type=brainstorm_define_task`);
        const data = res.data;
        const msgs = data?.messages || [];
        if (msgs.length > 0) {
          setSavedTaskDefinition(msgs[0]);
          setIsBrainstorming(true);
        }
      } catch (error) {
        // No existing conversation, proceed with define task flow
      }
    };
    checkExisting();
  }, [selectedNote?.id]);

  const scrollToBottom = useCallback(() => {
    if (contentRef.current) {
      setTimeout(() => {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }, 50);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [turns, initialResult, isLoading, scrollToBottom]);

  const handleSubmit = async () => {
    if (!taskInput.trim() || isLoading) return;

    if (!selectedNote?.id) {
      message.warning(t('messages:no_note_selected', { defaultValue: 'Please select a note first' }));
      return;
    }

    const currentMessage = taskInput.trim();
    setTaskInput('');
    setIsLoading(true);

    // First input becomes the taskDefinition; subsequent inputs go into conversation history
    const isFirstTurn = taskDefinition === null;
    const currentTaskDefinition = isFirstTurn ? currentMessage : taskDefinition;
    if (isFirstTurn) {
      setTaskDefinition(currentMessage);
    } else {
      // Only add to turns for follow-up messages (task definition is NOT a turn)
      setTurns(prev => [...prev, { userMessage: currentMessage, aiResult: null }]);
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;

      // Build conversation history as [Assistant response, User reply] pairs
      // The current user message is included as the last user reply
      const conversationHistory = [];
      if (!isFirstTurn && initialResult) {
        const completedTurns = turns.filter(t => t.aiResult);
        // Chain: initialResult → turns[0].userMessage → turns[0].aiResult → ... → currentMessage
        const aiResponses = [initialResult, ...completedTurns.map(t => t.aiResult)];
        const userReplies = [...completedTurns.map(t => t.userMessage), currentMessage];
        for (let i = 0; i < aiResponses.length; i++) {
          conversationHistory.push({
            aiResponse: formatResultForHistory(aiResponses[i]),
            userMessage: userReplies[i]
          });
        }
      }

      const response = await fetch(`${baseUrl}/notes/qa/brainstorm_define_task`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          noteId: selectedNote.id,
          taskDefinition: currentTaskDefinition,
          conversationHistory
        })
      });

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error('INSUFFICIENT_CREDITS');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (isFirstTurn) {
        // Store the initial AI result separately (not as a turn)
        setInitialResult(data);
      } else {
        // Update the last turn with the AI result
        setTurns(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { userMessage: currentMessage, aiResult: data };
          return updated;
        });
      }

    } catch (error) {
      console.error('[BrainstormPanel] Define task failed:', error);
      const msg = error.message === 'INSUFFICIENT_CREDITS'
        ? t('dashboard:insufficient_credits', { defaultValue: 'Insufficient credits. Please purchase more credits to continue.' })
        : t('messages:qa_service_unavailable', { defaultValue: 'Sorry, the service is temporarily unavailable. Please try again later.' });
      message.error(msg);
      if (isFirstTurn) {
        // Reset task definition on error so user can try again
        setTaskDefinition(null);
      } else {
        // Remove the pending turn on error
        setTurns(prev => prev.slice(0, -1));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const fontFamily = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  // Get the latest AI result for passing to session panel
  const getLatestTaskData = () => {
    const latestResult = turns.length > 0 && turns[turns.length - 1].aiResult
      ? turns[turns.length - 1].aiResult
      : initialResult;
    return {
      summary: latestResult?.rewrite?.summary || '',
      problem: latestResult?.rewrite?.problem || '',
      deliverable: latestResult?.rewrite?.deliverable || '',
    };
  };

  const renderAiResult = (aiResult) => (
    <>
      {/* Ready status */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        backgroundColor: aiResult.ready_to_brainstorm ? '#f0fdf4' : '#fffbeb',
        border: `1px solid ${aiResult.ready_to_brainstorm ? '#bbf7d0' : '#fde68a'}`,
        borderRadius: 8
      }}>
        {aiResult.ready_to_brainstorm ? (
          <CheckCircleOutlined style={{ fontSize: 14, color: '#16a34a' }} />
        ) : (
          <QuestionCircleOutlined style={{ fontSize: 14, color: '#d97706' }} />
        )}
        <span style={{
          fontSize: 12,
          fontWeight: 500,
          color: aiResult.ready_to_brainstorm ? '#166534' : '#92400e',
          fontFamily
        }}>
          {aiResult.ready_to_brainstorm
            ? t('dashboard:brainstorm_ready', { defaultValue: 'Ready to brainstorm!' })
            : t('dashboard:brainstorm_needs_clarification', { defaultValue: 'Needs clarification before brainstorming' })
          }
        </span>
      </div>

      {/* Summary */}
      {aiResult.rewrite?.summary && (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          padding: 14
        }}>
          <h4 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: '#374151', fontFamily, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('dashboard:brainstorm_summary', { defaultValue: 'Task Summary' })}
          </h4>
          <p style={{ margin: 0, fontSize: 14, lineHeight: '22px', color: '#111827', fontFamily }}>
            {aiResult.rewrite.summary}
          </p>
        </div>
      )}

      {/* Start Brainstorm button */}
      {aiResult.ready_to_brainstorm && (
        <button
          onClick={async () => {
            try {
              const baseUrl = process.env.REACT_APP_API_URL || '/api';
              const authStorage = localStorage.getItem('auth-storage');
              const token = authStorage ? JSON.parse(authStorage).state?.token : null;

              const taskData = getLatestTaskData();

              const res = await fetch(`${baseUrl}/notes/conversations/${selectedNote.id}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify({
                  type: 'brainstorm_define_task',
                  messages: [taskData]
                })
              });

              if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
              }

              message.success(t('dashboard:brainstorm_task_saved', { defaultValue: 'Task definition saved!' }));

              // Transition to brainstorm session panel
              setIsBrainstorming(true);
            } catch (error) {
              console.error('[BrainstormPanel] Save task definition failed:', error);
              message.error(t('messages:save_failed', { defaultValue: 'Failed to save. Please try again.' }));
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            width: '100%',
            padding: '10px 16px',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            fontFamily,
            cursor: 'pointer',
            transition: 'opacity 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <RocketOutlined style={{ fontSize: 16 }} />
          {t('dashboard:brainstorm_start', { defaultValue: 'Start Brainstorm' })}
        </button>
      )}

      {/* Clarifying Questions */}
      {aiResult.clarifying_questions?.length > 0 && (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          padding: 14
        }}>
          <h4 style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#374151', fontFamily, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('dashboard:brainstorm_questions', { defaultValue: 'Clarifying Questions' })}
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {aiResult.clarifying_questions.map((q, index) => (
              <div
                key={index}
                style={{
                  padding: 10,
                  backgroundColor: '#f9fafb',
                  borderRadius: 8,
                  border: '1px solid #f3f4f6'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: '#dbeafe',
                    color: '#2563eb',
                    fontSize: 11,
                    fontWeight: 600,
                    flexShrink: 0,
                    marginTop: 1
                  }}>
                    {index + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 14, lineHeight: '20px', fontWeight: 500, color: '#111827', fontFamily }}>
                      {q.question}
                    </p>
                    {q.why_it_matters && (
                      <p style={{ margin: '4px 0 0', fontSize: 12, lineHeight: '18px', color: '#6b7280', fontFamily, fontStyle: 'italic' }}>
                        {q.why_it_matters}
                      </p>
                    )}
                    <span style={{
                      display: 'inline-block',
                      marginTop: 6,
                      padding: '2px 8px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: 4,
                      fontSize: 11,
                      color: '#6b7280',
                      fontFamily
                    }}>
                      {q.about}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const handleRedefine = () => {
    Modal.confirm({
      title: t('dashboard:brainstorm_redefine_title', { defaultValue: 'Redefine Task?' }),
      content: t('dashboard:brainstorm_redefine_warning', {
        defaultValue: 'This will go back to the define task stage. All brainstorming conversation and task definition data will be erased. This action cannot be undone.'
      }),
      okText: t('common:ok', { defaultValue: 'Confirm' }),
      cancelText: t('common:cancel_btn', { defaultValue: 'Cancel' }),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const baseUrl = process.env.REACT_APP_API_URL || '/api';
          const authStorage = localStorage.getItem('auth-storage');
          const token = authStorage ? JSON.parse(authStorage).state?.token : null;
          const headers = { 'Authorization': token ? `Bearer ${token}` : '' };

          // Delete brainstorm, brainstorm_define_task, and brainstorm_config conversations
          await Promise.all([
            fetch(`${baseUrl}/notes/conversations/${selectedNote.id}?type=brainstorm`, {
              method: 'DELETE', headers
            }),
            fetch(`${baseUrl}/notes/conversations/${selectedNote.id}?type=brainstorm_define_task`, {
              method: 'DELETE', headers
            }),
            fetch(`${baseUrl}/notes/conversations/${selectedNote.id}?type=brainstorm_config`, {
              method: 'DELETE', headers
            }),
          ]);
        } catch (error) {
          console.error('[BrainstormPanel] Failed to delete conversations:', error);
        }

        // Reset all local state
        setIsBrainstorming(false);
        setSavedTaskDefinition(null);
        setTaskDefinition(null);
        setInitialResult(null);
        setTurns([]);
        setTaskInput('');
      },
    });
  };

  // Brainstorm session panel
  if (isBrainstorming) {
    return (
      <BrainstormSessionPanel
        onRedefine={handleRedefine}
        selectedNote={selectedNote}
        taskDefinition={savedTaskDefinition || getLatestTaskData()}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fafafa' }}>
      <style>{`
        @keyframes brainstormBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>

      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <RocketOutlined style={{ fontSize: 16, color: '#fff' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: '20px', fontFamily, WebkitFontSmoothing: 'antialiased' }}>
              {t('dashboard:ai_brainstorm', { defaultValue: 'Ideas Brainstorm' })} - {t('dashboard:brainstorm_stage_define', { defaultValue: 'Define Task' })}
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: '#4b5563', lineHeight: '16px', fontFamily, WebkitFontSmoothing: 'antialiased' }}>
              {t('dashboard:ai_brainstorm_desc', { defaultValue: 'Expand ideas with AI knowledge and your own knowledge hub' })}
            </p>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div
        ref={contentRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 20
        }}
      >
        {/* Welcome / instruction (only when no task definition yet) */}
        {!taskDefinition && !isLoading && (
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: 12,
            padding: 16
          }}>
            <p style={{ margin: 0, fontSize: 14, lineHeight: '22px', color: '#166534', fontFamily }}>
              {t('dashboard:brainstorm_instruction', {
                defaultValue: 'Describe what you want to brainstorm. AI will analyze your task, clarify the problem and deliverable, and ask questions if needed before starting the brainstorm.'
              })}
            </p>
          </div>
        )}

        {/* Task Definition Card (fixed, not a turn) */}
        {taskDefinition && (
          <div style={{
            backgroundColor: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: 12,
            padding: 16
          }}>
            <h4 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: '#065f46', fontFamily, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('dashboard:brainstorm_task_definition', { defaultValue: 'Task Definition' })}
            </h4>
            <p style={{ margin: 0, fontSize: 14, lineHeight: '22px', color: '#111827', fontFamily, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {taskDefinition}
            </p>
          </div>
        )}

        {/* Initial AI response (tied to task definition, not a turn) */}
        {initialResult && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <RocketOutlined style={{ fontSize: 16, color: '#059669' }} />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {renderAiResult(initialResult)}
            </div>
          </div>
        )}

        {/* Loading state for initial task definition */}
        {taskDefinition && !initialResult && isLoading && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 16,
            backgroundColor: '#f3f4f6',
            borderRadius: 12
          }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <RocketOutlined style={{ fontSize: 16, color: '#059669' }} />
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#6b7280', fontFamily }}>
                {t('dashboard:brainstorm_analyzing', { defaultValue: 'Analyzing your task...' })}
              </span>
              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'brainstormBounce 1s infinite', animationDelay: '0ms' }} />
              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'brainstormBounce 1s infinite', animationDelay: '150ms' }} />
              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'brainstormBounce 1s infinite', animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        {/* Follow-up conversation turns */}
        {turns.map((turn, turnIndex) => (
          <div key={turnIndex} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* User message */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <div style={{
                maxWidth: '85%',
                borderRadius: 16,
                padding: '12px 16px',
                backgroundColor: '#d1fae5',
                color: '#111827'
              }}>
                <p style={{ margin: 0, fontSize: 14, lineHeight: '20px', fontFamily, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {turn.userMessage}
                </p>
              </div>
              <div style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 500,
                color: '#374151'
              }}>
                {t('common:you_short', { defaultValue: 'You' })}
              </div>
            </div>

            {/* AI response */}
            {turn.aiResult ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: '#d1fae5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <RocketOutlined style={{ fontSize: 16, color: '#059669' }} />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {renderAiResult(turn.aiResult)}
                </div>
              </div>
            ) : (
              /* Loading state for pending turn */
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 16,
                backgroundColor: '#f3f4f6',
                borderRadius: 12
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: '#d1fae5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <RocketOutlined style={{ fontSize: 16, color: '#059669' }} />
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span style={{ fontSize: 14, color: '#6b7280', fontFamily }}>
                    {t('dashboard:brainstorm_analyzing', { defaultValue: 'Analyzing your task...' })}
                  </span>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'brainstormBounce 1s infinite', animationDelay: '0ms' }} />
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'brainstormBounce 1s infinite', animationDelay: '150ms' }} />
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'brainstormBounce 1s infinite', animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Input Area */}
      <div style={{
        borderTop: '1px solid #e5e7eb',
        padding: 16,
        display: 'flex',
        gap: 8
      }}>
        <TextArea
          value={taskInput}
          onChange={(e) => setTaskInput(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={
            !taskDefinition
              ? t('dashboard:brainstorm_task_placeholder', { defaultValue: 'Describe what you want to brainstorm...' })
              : t('dashboard:brainstorm_reply_placeholder', { defaultValue: 'Answer the questions or refine your task...' })
          }
          autoSize={{ minRows: 2, maxRows: 5 }}
          style={{
            flex: 1,
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            lineHeight: '20px',
            resize: 'none',
            border: '1px solid #d1d5db',
            fontFamily
          }}
        />
        <button
          onClick={handleSubmit}
          disabled={!taskInput.trim() || isLoading}
          style={{
            width: 44,
            height: 44,
            borderRadius: 8,
            border: 'none',
            backgroundColor: taskInput.trim() && !isLoading ? '#059669' : '#e5e7eb',
            color: taskInput.trim() && !isLoading ? '#fff' : '#9ca3af',
            cursor: taskInput.trim() && !isLoading ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            alignSelf: 'flex-end'
          }}
        >
          <SendOutlined style={{ fontSize: 18 }} />
        </button>
      </div>
    </div>
  );
};

export default BrainstormPanel;
